import { reactive } from 'vue'
import { has, set, get, map, unset } from 'lodash'

/**
 * Provides error handling functionality using Vue's Composition API.
 * @returns {Object} Methods and reactive state for handling form errors and validation.
 */
export function useErrorHandler () {
  // Reactive state for violations
  const violations = reactive({})

  /**
     * Build violations from the error object.
     * @param {Object} err - Error object containing the violations.
     */
  function buildViolations (err) {
    if (!has(err, 'data.violations')) {
      return
    }

    const newViolations = {}

    map(err.data.violations, (value) => {
      set(newViolations, value.propertyPath, value.title)
    })

    Object.assign(violations, newViolations)
  }

  /**
     * Build a violation at the custom propertyPath.
     * @param {Object} err - Error object.
     * @param {string} propertyPath - The property path to set the violation.
     */
  function buildViolation (err, propertyPath) {
    let message = ''

    if (has(err, 'data.title')) {
      message = message + err.data.title
    }

    if (has(err, 'data.detail')) {
      message = message + ': ' + err.data.detail
    }

    if (!has(violations, propertyPath)) {
      set(violations, propertyPath, [])
    }

    violations[propertyPath].push(message)
  }

  /**
     * Clear all violations.
     */
  function clearViolations () {
    Object.keys(violations).forEach((key) => {
      unset(violations, key)
    })
  }

  /**
     * Clear a violation at the given property path.
     * @param {string} propertyPath - The property path to clear the violation.
     */
  function clearViolation (propertyPath) {
    unset(violations, propertyPath)
  }

  /**
     * Check if there is a violation for the given propertyPath.
     * @param {string} propertyPath - The property path to check.
     * @returns {boolean} Whether a violation exists at the propertyPath.
     */
  function hasViolation (propertyPath) {
    if (!propertyPath) {
      return false
    }

    return has(violations, propertyPath)
  }

  /**
     * Get the violation at the property path.
     * @param {string} propertyPath - The property path to get the violation.
     * @returns {string|boolean} The violation message or false if not found.
     */
  function getViolation (propertyPath) {
    if (!propertyPath) {
      return false
    }
    return get(violations, propertyPath)
  }

  /**
     * Get multiple violations at the given property paths.
     * @param {string[]} propertyPaths - The property paths to get the violations.
     * @returns {Object|Array} An object of violations or an empty array if none.
     */
  function getViolations (propertyPaths) {
    if (!propertyPaths) {
      return []
    }

    const result = {}

    map(propertyPaths, (value) => {
      set(result, value, get(violations, value))
    })

    return result
  }

  return {
    violations,
    buildViolations,
    buildViolation,
    clearViolations,
    clearViolation,
    hasViolation,
    getViolation,
    getViolations
  }
}
